import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import api from "@/lib/api";
import { fmtSize, sheetUrl } from "./DatasheetViewer";

const EMPTY = { datasheet_storage_path: null, datasheet_filename: null, datasheet_size_bytes: null, datasheet_uploaded_at: null };
const ver = (ts) => (ts || "").replace(/\D/g, "").slice(0, 14);

// Admin: Datenblatt-PDF je PV-Komponente – Vertrieb kann es im Projekt anzeigen.
export function PvDatasheetField({ editor, onChange }) {
  const [busy, setBusy] = useState(false);
  const has = !!editor.datasheet_storage_path;

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name?.toLowerCase().endsWith(".pdf")) { toast.error("Nur PDF-Dateien erlaubt"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Datei zu groß — max. 25 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pv/catalog/${editor.id}/datasheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(Object.fromEntries(Object.entries(data).filter(([k]) => k.startsWith("datasheet_"))));
      toast.success("Datenblatt hochgeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Datenblatt wirklich entfernen?")) return;
    try {
      await api.delete(`/pv/catalog/${editor.id}/datasheet`);
      onChange(EMPTY);
      toast.success("Datenblatt entfernt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen");
    }
  };

  return (
    <div data-testid="pv-catalog-datasheet">
      <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Datenblatt-PDF (optional) — Vertrieb kann es im Projekt zeigen</label>
      <div className="flex items-center gap-2 flex-wrap">
        <label className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg font-bold text-[12px] cursor-pointer transition-colors ${busy ? "bg-zinc-700 text-zinc-400 cursor-wait" : "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]"}`}
          data-testid="pv-catalog-datasheet-upload-label">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {has ? "Datenblatt austauschen" : "Datenblatt hochladen"}
          <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={busy}
            data-testid="pv-catalog-datasheet-file"
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {has && (
          <>
            <a href={sheetUrl(`/api/public/pv-components/${editor.id}/datasheet?v=${ver(editor.datasheet_uploaded_at)}`)}
              target="_blank" rel="noreferrer" data-testid="pv-catalog-datasheet-preview"
              className="h-10 px-3 inline-flex items-center rounded-lg bg-[#18181B] border border-zinc-700 text-[12px] font-medium text-zinc-200 hover:border-[#00E575]">
              Vorschau
            </a>
            <button type="button" onClick={remove} data-testid="pv-catalog-datasheet-remove"
              className="h-10 px-3 inline-flex items-center rounded-lg bg-zinc-800 border border-zinc-700 text-[12px] font-medium text-red-400 hover:bg-red-900/30">
              Entfernen
            </button>
          </>
        )}
      </div>
      <p className="text-[12px] text-zinc-400 mt-1" data-testid="pv-catalog-datasheet-info">
        {has
          ? <><b className="text-zinc-200">{editor.datasheet_filename || "Datenblatt.pdf"}</b>{editor.datasheet_size_bytes ? ` · ${fmtSize(editor.datasheet_size_bytes)}` : ""}</>
          : "Optional. Sobald hochgeladen, erscheint im PV-Konfigurator „Datenblatt zeigen“ für alle Nutzer."}
      </p>
    </div>
  );
}
